import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import apiClient from '../api/api';
import { 
    FaUserCircle, FaTachometerAlt, FaExchangeAlt, FaWallet, 
    FaSignOutAlt, FaChevronRight, FaShieldAlt, FaQuestionCircle, 
    FaHeadset, FaBriefcase, FaGavel, FaStar, FaTools, FaClipboardList, FaEnvelope,
    FaUsers, FaUserShield, FaCogs, FaMoneyBillWave, FaPlus
} from 'react-icons/fa';
import loadImage from '../assets/imageLoader';

const MobileMenuPage = () => {
    const { user, logout, refetchUser } = useAuth();
    const { unreadMessageCount } = useSocket();
    const navigate = useNavigate();

    const handleSwitchProfile = async () => {
        try {
            const res = await apiClient.post('/auth/switch-role');
            if (res.data.token) localStorage.setItem('token', res.data.token);
            await refetchUser();
            window.location.reload();
        } catch (err) {
            alert("Failed to switch profile.");
        }
    };

    const MenuItem = ({ icon: Icon, label, onClick, to, color = "text-slate-600", extra, badge }) => {
        const content = (
            <div className="flex items-center justify-between p-4 bg-white rounded-2xl border border-slate-100 shadow-sm active:scale-[0.98] transition-all">
                <div className="flex items-center gap-4">
                    <div className={`w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center ${color}`}>
                        <Icon size={16} />
                    </div>
                    <div>
                        <p className="text-sm font-bold text-slate-800">{label}</p>
                        {extra && <p className="text-[9px] text-slate-400 font-bold uppercase">{extra}</p>}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {badge > 0 && (
                        <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center shadow-sm">
                            {badge}
                        </span>
                    )}
                    <FaChevronRight className="text-slate-300" size={10} />
                </div>
            </div>
        );

        if (to) return <Link to={to} className="block">{content}</Link>;
        return <div onClick={onClick} className="cursor-pointer">{content}</div>;
    };

    // --- Role Specific Sections ---
    const workerLinks = [
        { label: 'My Jobs', to: '/worker-dashboard/running-jobs', icon: FaBriefcase, color: 'text-blue-500' },
        { label: 'My Activities (Bids)', to: '/worker-dashboard/bids', icon: FaGavel, color: 'text-purple-500' },
        { label: 'My Ratings', to: '/worker-dashboard/ratings', icon: FaStar, color: 'text-yellow-500' },
        { label: 'My Instruments', to: '/worker-dashboard/instruments', icon: FaTools, color: 'text-emerald-500' },
    ];

    const buyerLinks = [
        { label: 'My Job Posts', to: '/my-jobs', icon: FaClipboardList, color: 'text-blue-500' },
        { label: 'Manage Hires', to: '/my-hires', icon: FaBriefcase, color: 'text-emerald-500' },
        { label: 'Post a New Job', to: '/publish-job', icon: FaPlus, color: 'text-purple-500' },
    ];

    const adminLinks = [
        { label: 'User Management', to: '/admin/users', icon: FaUsers, color: 'text-blue-600' },
        { label: 'Category Hierarchy', to: '/admin/categories', icon: FaShieldAlt, color: 'text-emerald-600' },
        { label: 'Form Configuration', to: '/admin/forms', icon: FaCogs, color: 'text-slate-600' },
        { label: 'KYC Verifications', to: '/admin/verifications', icon: FaUserShield, color: 'text-orange-600' },
        { label: 'Payout Requests', to: '/admin/withdrawals', icon: FaMoneyBillWave, color: 'text-red-600' },
        { label: 'Global Job Oversight', to: '/admin/jobs', icon: FaBriefcase, color: 'text-indigo-600' },
        { label: 'System Wallet', to: '/admin/wallet', icon: FaWallet, color: 'text-slate-800' },
    ];

    const isAdmin = user?.role === 'admin';
    const roleLinks = isAdmin ? adminLinks : (user?.role === 'buyer' ? buyerLinks : workerLinks);

    return (
        <div className="min-h-screen bg-slate-50 p-6 font-sans pb-32">
            <div className="flex items-center justify-between mb-8">
                <h1 className="text-3xl font-black text-slate-900 tracking-tight">Menu</h1>
                <button 
                    onClick={() => logout(navigate)}
                    className="p-3 bg-red-50 text-red-500 rounded-xl"
                >
                    <FaSignOutAlt />
                </button>
            </div>

            {/* Profile Header Card */}
            <div className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-xl shadow-slate-200/50 mb-8 flex items-center justify-between group active:scale-95 transition-all">
                <div className="flex items-center gap-5">
                    <img src={loadImage(user?.picture)} className="w-16 h-16 rounded-2xl object-cover border-2 border-slate-100" alt="Profile" />
                    <div>
                        <h2 className="text-xl font-black text-slate-800">{user?.name}</h2>
                        <p className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded w-fit ${isAdmin ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>
                            {isAdmin ? 'System Admin' : `${user?.role} Mode`}
                        </p>
                    </div>
                </div>
            </div>

            <div className="space-y-6">
                {/* Main Section */}
                <div className="space-y-3">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 mb-2">
                        {isAdmin ? 'Administration' : 'Workspace'}
                    </p>
                    
                    <MenuItem 
                        icon={FaTachometerAlt} 
                        label={isAdmin ? 'Admin Dashboard' : 'Dashboard Overview'} 
                        to={isAdmin ? '/admin' : (user?.role === 'buyer' ? '/owner-dashboard' : '/worker-dashboard')} 
                        color={isAdmin ? 'text-red-600' : 'text-emerald-500'} 
                    />

                    {roleLinks.map((link, i) => (
                        <MenuItem key={i} icon={link.icon} label={link.label} to={link.to} color={link.color} />
                    ))}

                    {!isAdmin && (
                        <>
                            <MenuItem icon={FaWallet} label="Wallet & Balance" to="/wallet" color="text-indigo-500" />
                            <MenuItem icon={FaEnvelope} label="Messages / Chat" to="/messages" color="text-blue-400" badge={unreadMessageCount} />
                        </>
                    )}
                </div>

                {/* Management Section */}
                {!isAdmin && (
                    <div className="space-y-3">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 mb-2">Account Management</p>
                        <MenuItem 
                            icon={FaExchangeAlt} 
                            label={`Switch to ${user?.role === 'worker' ? 'Buyer' : 'Worker'}`} 
                            onClick={handleSwitchProfile} 
                            color="text-orange-500"
                            extra="Toggle Role"
                        />
                    </div>
                )}
                
                {/* Help Section */}
                <div className="space-y-3">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4 mb-2">Support</p>
                    <MenuItem icon={FaQuestionCircle} label="Frequently Asked Questions" to="/faq" />
                    <MenuItem icon={FaHeadset} label="Contact Customer Care" to="/contact" />
                    {!isAdmin && <MenuItem icon={FaShieldAlt} label="Community Guidelines" to="/guide" />}
                </div>

                <div className="pt-6 text-center">
                    <p className="text-[10px] font-bold text-slate-300 uppercase tracking-[0.3em] mb-4">Service24 v1.0</p>
                    <button 
                        onClick={() => logout(navigate)}
                        className="w-full p-5 bg-white text-slate-400 rounded-2xl font-bold flex items-center justify-center gap-3 border border-slate-100 active:scale-95 transition-all"
                    >
                        Sign Out of Service24
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MobileMenuPage;
